import React, { useState } from 'react';
import { Icon, IconFlip } from '@progress/kendo-react-common';
import { Reveal } from '@progress/kendo-react-animation';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatReasoning as ChatReasoningType, ReasoningStep } from '../../types';

interface ChatReasoningProps {
    reasoning: ChatReasoningType;
    isLoading: boolean;
}

export const ChatReasoningSection: React.FC<ChatReasoningProps> = ({ 
    reasoning, 
    isLoading 
}) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [iconFlip, setIconFlip] = useState<IconFlip>('default');

    const toggleExpanded = () => {
        setIsExpanded(prev => !prev);
        setIconFlip(prev => prev === 'default' ? 'vertical' : 'default');
    };

    const hasSteps = reasoning?.steps && reasoning.steps.length > 0;

    const renderTimeInfo = () => {
        if (isLoading) {
            return (
                <>
                    <Icon name="loading" />
                    <span className='chat-reasoning-time-text'>Reasoning...</span>
                </>
            );
        }
        
        if (reasoning.timeInSeconds) {
            return (
                <>
                    <Icon name="sparkles" />
                    <span className='chat-reasoning-time-text'>
                        Reasoned for {reasoning.timeInSeconds} seconds
                    </span>
                </>
            );
        }
        
        if (hasSteps) {
            return (
                <>
                    <Icon name="sparkles" />
                    <span className='chat-reasoning-time-text'>
                        {reasoning.steps.length} reasoning steps
                    </span>
                </>
            );
        }
        
        return null;
    };

    const renderStepsList = () => {
        if (!isExpanded || !hasSteps) return null;

        return (
            <ul className='chat-reasoning-steps'>
                {reasoning.steps.map((step: ReasoningStep, index: number) => (
                    <li className='chat-reasoning-step' key={index}>
                        <span className='chat-reasoning-step-number'>{index + 1}</span>
                        <div className='chat-reasoning-step-info'>
                            <h4 style={{ textTransform: 'capitalize' }}>{step.title}</h4>
                            <div className='chat-reasoning-step-value'>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {step.reason}
                                </ReactMarkdown>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <div className='chat-reasoning'>
            <div className='chat-reasoning-header'>
                <span className='chat-reasoning-time'>
                    {renderTimeInfo()}
                </span>
                {hasSteps && (
                    <Icon 
                        flip={iconFlip} 
                        name="chevron-down" 
                        onClick={toggleExpanded} 
                    />
                )}
            </div>
            <Reveal componentChildClassName="chat-reasoning-steps-container">
                {renderStepsList()}
            </Reveal>
        </div>
    );
};
